// FTEDN — Auth state management (Zustand)
import { create } from 'zustand';
import type { AuthenticatedUser } from '@ftedn/shared';
import { apiClient } from '../hooks/useApi';
import {
  auth,
  createUserWithEmailAndPassword,
  googleProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  updateProfile,
  type ApplicationVerifier,
  type ConfirmationResult,
} from '../services/firebase';

interface AuthState {
  token: string | null;
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  confirmationResult: ConfirmationResult | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: (referralCode?: string) => Promise<boolean>;
  signup: (email: string, password: string, displayName: string, referralCode?: string) => Promise<boolean>;
  startPhoneSignup: (phoneNumber: string, verifier: ApplicationVerifier) => Promise<boolean>;
  confirmPhoneSignup: (code: string, displayName: string, referralCode?: string) => Promise<boolean>;
  skipPhonePrompt: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => () => void;
}

async function bootstrapSession(payload?: { displayName?: string; phone?: string | null }) {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) throw new Error('No Firebase user is signed in.');

  let token = await firebaseUser.getIdToken();
  localStorage.setItem('ftedn_token', token);

  const response = await apiClient.post('/auth/bootstrap', payload);
  const user = response.data?.user as AuthenticatedUser | undefined;
  if (!user) {
    throw new Error('Auth bootstrap did not return a user.');
  }

  await firebaseUser.getIdToken(true);
  token = await firebaseUser.getIdToken();
  localStorage.setItem('ftedn_token', token);
  return { token, user };
}

async function claimReferralInvite(referralCode?: string) {
  if (!referralCode) return;

  try {
    await apiClient.post('/referral/claim', { referralCode });
  } catch (err) {
    // Signup must not be blocked by invalid, self, or already-claimed invites.
    console.warn('[auth] referral claim skipped:', err);
  }
}

async function hydrateSession() {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) return null;

  const token = await firebaseUser.getIdToken();
  localStorage.setItem('ftedn_token', token);

  const response = await apiClient.get('/auth/me');
  const user = response.data?.user as AuthenticatedUser | undefined;
  if (!user) {
    throw new Error('Auth session did not return a user.');
  }

  return { token, user };
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  confirmationResult: null,

  login: async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const session = await bootstrapSession();
      set({
        token: session.token,
        user: session.user,
        isAuthenticated: true,
      });
      return true;
    } catch (err) {
      console.error('[auth] login error:', err);
      await signOut(auth).catch(() => undefined);
      localStorage.removeItem('ftedn_token');
      set({ token: null, user: null, isAuthenticated: false });
      return false;
    }
  },

  loginWithGoogle: async (referralCode) => {
    try {
      await signInWithPopup(auth, googleProvider);
      const session = await bootstrapSession();
      await claimReferralInvite(referralCode);
      set({
        token: session.token,
        user: session.user,
        isAuthenticated: true,
      });
      return true;
    } catch (err) {
      console.error('[auth] Google login error:', err);
      await signOut(auth).catch(() => undefined);
      localStorage.removeItem('ftedn_token');
      set({ token: null, user: null, isAuthenticated: false });
      return false;
    }
  },

  signup: async (email, password, displayName, referralCode) => {
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(credential.user, { displayName });
      const session = await bootstrapSession({ displayName });
      await claimReferralInvite(referralCode);
      set({
        token: session.token,
        user: session.user,
        isAuthenticated: true,
      });
      return true;
    } catch {
      await signOut(auth).catch(() => undefined);
      localStorage.removeItem('ftedn_token');
      set({ token: null, user: null, isAuthenticated: false });
      return false;
    }
  },

  startPhoneSignup: async (phoneNumber, verifier) => {
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      set({ confirmationResult });
      return true;
    } catch {
      set({ confirmationResult: null });
      return false;
    }
  },

  confirmPhoneSignup: async (code, displayName, referralCode) => {
    const confirmationResult = useAuthStore.getState().confirmationResult;

    if (!confirmationResult) {
      return false;
    }

    try {
      const credential = await confirmationResult.confirm(code);
      if (displayName.trim()) {
        await updateProfile(credential.user, { displayName: displayName.trim() });
      }

      const session = await bootstrapSession({
        displayName: displayName.trim(),
        phone: credential.user.phoneNumber,
      });
      await claimReferralInvite(referralCode);

      set({
        token: session.token,
        user: session.user,
        isAuthenticated: true,
        confirmationResult: null,
      });
      return true;
    } catch {
      await signOut(auth).catch(() => undefined);
      localStorage.removeItem('ftedn_token');
      set({ token: null, user: null, isAuthenticated: false, confirmationResult: null });
      return false;
    }
  },

  skipPhonePrompt: async () => {
    const response = await apiClient.post('/auth/phone-prompt/skip');
    const user = (response.data?.user as AuthenticatedUser | undefined) ?? useAuthStore.getState().user;
    set({ user });
  },

  logout: async () => {
    await signOut(auth);
    localStorage.removeItem('ftedn_token');
    set({ token: null, user: null, isAuthenticated: false, confirmationResult: null });
  },

  checkAuth: () => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        localStorage.removeItem('ftedn_token');
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          confirmationResult: null,
        });
        return;
      }

      set({ isLoading: true });

      try {
        const session = await hydrateSession();
        set({
          token: session?.token ?? null,
          user: session?.user ?? null,
          isAuthenticated: Boolean(session?.token),
          isLoading: false,
        });
      } catch (err) {
        console.error('[auth] session hydrate error:', err);
        localStorage.removeItem('ftedn_token');
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          confirmationResult: null,
        });
      }
    });

    return unsubscribe;
  },
}));
